import React, { useEffect, useState, useRef } from 'react';
import { Redirect, Route } from 'react-router';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { InnerhivePage } from '../InnerhivePage';
import { ActiveCareMapProviderV2 } from '../ActiveCareMapProvider';
import { CalendarTabsProvider } from '../appointments/CalendarTabsProvider';
import { CareMapState } from '../caremap/CareMapState';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { CreateAccountButton } from '../auth/CreateAccountButton';
import { AddCareMapButton } from '../caremap/AddCareMapButton';
import { CareRecipientSwitcher } from '../navigation/CareRecipientSwitcher';
import { IonButtons, IonToolbar } from '@ionic/react';
import { FullWidthHeader } from '../components/FullWidthHeader';
import { TasksList } from './TasksList';
import { AddTaskButton } from './AddTaskButton';
import { TasksState, ITask } from './TasksState';
import { TaskEditorModal, TaskEditorModalComponent } from './TaskEditorModal';
import { TasksFilterControl, TasksFilterState } from './TasksFilterControl';
import { useBreakpoints } from '../theme/useBreakpoints';
import { HistoryViewModel } from '../support/HistoryViewModel';
import dayjs from 'dayjs';
import { DevSettings } from '../support/DevSettings';
import { TasksNotificationDevTools } from './TasksNotificationDevTools';

export const TasksPageRouteHandler: React.FC = () => {
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);
    const history = useInstance(HistoryViewModel);

    const recentCareMap = careMapState.getMostRecentCaremap();
    const incompleteCareMap = careMapState.getIncompleteCareMap();

    return (
        <>
            <Route exact path="/calendar/tasks" render={() => {
                // Preserve query params when redirecting to caremap-specific route
                const currentSearch = history.getCurrentLocation().search || '';
                if (recentCareMap && !incompleteCareMap) {
                    return <Redirect to={`/calendar/tasks/${recentCareMap.careMapId}${currentSearch}`} />;
                } else {
                    return <TasksNoCareMapPage />;
                }
            }} />

            <Route exact path="/calendar/tasks/:caremapId" render={routeParams => (
                <ActiveCareMapProviderV2 careMapId={routeParams.match.params.caremapId}>
                    <ActiveCaremapTasksPage />
                </ActiveCareMapProviderV2>
            )} />
        </>
    );
};

const emptyImage = <img src="/assets/images/calendar/empty-appointments.svg" alt="no tasks" style={{width: '100%'}} />;

export const TasksNoCareMapPage: React.FC = () => {
    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));
    const tabsProvider = useInstance(CalendarTabsProvider);

    return (
        <InnerhivePage title="Calendar" tabs={tabsProvider.getTabs(undefined)} skipRequireName>
            {isAnonymous && (
                <>
                    <CreateAccountButton trackingFrom="Tasks" />
                    <p>Create an account to manage your care tasks and stay organized!</p>
                </>
            )}
            {!isAnonymous && (
                <>
                    <AddCareMapButton />
                    <p>Add a care recipient to manage your care tasks and stay organized!</p>
                    {emptyImage}
                </>
            )}
        </InnerhivePage>
    );
};

export const ActiveCaremapTasksPage: React.FC = () => {
    const mixpanel = useInstance(MixPanelEventEmitter);
    const tabsProvider = useInstance(CalendarTabsProvider);
    const careMapState = useInstance(CareMapState);
    const tasksState = useInstance(TasksState);
    const taskEditorModal = useInstance(TaskEditorModal);
    const history = useInstance(HistoryViewModel);
    const devSettings = useInstance(DevSettings);

    useSubscription(() => tasksState);
    useSubscription(() => taskEditorModal);
    useSubscription(() => history);

    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));
    const heightOffset = 80 + (isAnonymous ? 28 : 0);

    const [filters, setFilters] = useState<TasksFilterState>({
        tags: [],
        sourceTypes: []
    });

    const [highlightTaskId, setHighlightTaskId] = useState<string | undefined>(undefined);

    const { isBelowBreakpoint } = useBreakpoints();
    const isMobile = isBelowBreakpoint('md');

    // Track if we've already sent the "Tasks Tab Opened" event
    const hasTrackedRef = useRef(false);

    // Handle highlight task from notification deep link
    useEffect(() => {
        const taskIdToHighlight = history.popSearchParam({ key: 'highlightTaskId' });
        if (taskIdToHighlight) {
            setHighlightTaskId(taskIdToHighlight);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle opening task editor from query parameter
    useEffect(() => {
        const searchParams = history.getCurrentSearchParams();
        const editTaskId = searchParams.get('edit');
        const source = searchParams.get('source') as 'add-button' | 'row-click' | 'menu-edit' | 'note-view' | null;

        if (editTaskId && tasksState.isLoaded() && !taskEditorModal.state.isOpen) {
            // Open the editor for this task
            taskEditorModal.open({ taskId: editTaskId, source: source || 'row-click' });

            // Clear the query parameters
            searchParams.delete('edit');
            searchParams.delete('source');
            const newSearch = searchParams.toString();
            const newUrl = `${history.getCurrentLocation().pathname}${newSearch ? '?' + newSearch : ''}`;
            history.replace(newUrl);
        }
    }, [history, tasksState, taskEditorModal]); // eslint-disable-line react-hooks/exhaustive-deps

    // Get tasks for filtering
    const allTasks = tasksState.getTasks();


    // Apply filters
    const filteredTasks = allTasks.filter(task => {
        // Apply tag filters
        if (filters.tags.length > 0) {
            if (!task.tags || task.tags.length === 0) return false;

            const hasMatchingTag = filters.tags.some(filterTag =>
                task.tags!.some(taskTag =>
                    taskTag.toLowerCase().includes(filterTag.toLowerCase())
                )
            );
            if (!hasMatchingTag) return false;
        }

        // Apply source type filters
        if (filters.sourceTypes.length > 0) {
            if (!task.source || !task.source.type) return false;

            const hasMatchingSourceType = filters.sourceTypes.includes(task.source.type);
            if (!hasMatchingSourceType) return false;
        }

        return true;
    });

    useEffect(() => {
        // Only track once tasks are loaded and we haven't tracked yet
        if (!tasksState.isLoaded() || hasTrackedRef.current) {
            return;
        }

        hasTrackedRef.current = true;

        // Status categories (mutually exclusive, sum to total)
        const completedTasks = allTasks.filter(t => t.completed).length;
        const skippedTasks = allTasks.filter(t => !t.completed && t.skippedAt).length;
        const overdueTasks = allTasks.filter(t =>
            !t.completed && !t.skippedAt && t.dueDate &&
            dayjs(t.dueDate).isBefore(dayjs(), 'day')
        ).length;
        const openTasks = allTasks.filter(t =>
            !t.completed && !t.skippedAt &&
            (!t.dueDate || !dayjs(t.dueDate).isBefore(dayjs(), 'day'))
        ).length;

        // Recurring task metrics
        const tasksWithRecurrence = allTasks.filter(t =>
            t.recurrenceRule || t.recurringFromTaskId
        ).length;
        const activeRecurringSeries = allTasks.filter(t =>
            (t.recurrenceRule || t.recurringFromTaskId) && // Is part of a recurring series
            !t.completed && !t.skippedAt                   // Is currently active
        ).length;

        // AI-generated task metrics
        const aiGeneratedTasks = allTasks.filter(t => t.source?.type === 'ai-generated').length;

        mixpanel.track('Tasks Tab Opened', () => ({
            'Total Tasks': allTasks.length,
            'Completed Tasks': completedTasks,
            'Skipped Tasks': skippedTasks,
            'Overdue Tasks': overdueTasks,
            'Open Tasks': openTasks,
            'Tasks With Recurrence': tasksWithRecurrence,
            'Active Recurring Series': activeRecurringSeries,
            'AI Generated Tasks': aiGeneratedTasks
        }));
    }, [tasksState, allTasks, mixpanel]); // Re-run when loaded state or task count changes

    const incompleteCareMap = careMapState.getIncompleteCareMap();

    const handleAddTask = (dueDate?: string, source?: 'add-button' | 'task-review-card') => {
        taskEditorModal.open({ defaultDueDate: dueDate, source: source || 'add-button' });
    };

    const handleEditTask = (taskId: string, source: 'row-click' | 'menu-edit' = 'row-click') => {
        taskEditorModal.open({ taskId, source });
    };

    const handleSaveTask = async (task: Partial<ITask>, options?: { reminderEnabled?: boolean }): Promise<string | undefined> => {
        if (taskEditorModal.state.mode === 'edit' && task.taskId) {
            await tasksState.updateTask(task.taskId, task);
            return task.taskId;
        } else {
            // Use the default due date if provided, otherwise let addTask use its default (today)
            const taskWithDueDate = {
                ...task,
                dueDate: task.dueDate || taskEditorModal.state.defaultDueDate
            };

            // Track source for task creation from review card
            const openedFrom = taskEditorModal.getOpenedFrom?.() || 'add-button';
            if (openedFrom === 'task-review-card') {
                taskWithDueDate.source = { type: 'manual-from-review-card' };
            }

            const createdTask = await tasksState.addTask(taskWithDueDate, options);
            return createdTask?.taskId;
        }
    };

    const handleDeleteTask = async (taskId: string) => {
        await tasksState.deleteTask(taskId);
    };

    const getTaskForEdit = () => {
        if (taskEditorModal.state.taskId) {
            return tasksState.getTasks().find(t => t.taskId === taskEditorModal.state.taskId);
        }
        return undefined;
    };

    const activeCaremap = careMapState.getActiveCaremap();

    // Permission guard: redirect if user doesn't have tasks permission
    if (activeCaremap && !activeCaremap.links?.tasks) {
        return <Redirect to="/calendar/appointments" />;
    }

    return (
        <>
            <InnerhivePage title="Calendar" tabs={tabsProvider.getTabs(activeCaremap)} data-id="tasks-page">
                <FullWidthHeader fixed>
                    <IonToolbar>
                        {incompleteCareMap ? (
                            <AddCareMapButton />
                        ) : (
                            <>
                                <div style={{margin: '4px 12px 0 4px', paddingTop: 5, paddingBottom: 10}}>
                                    <CareRecipientSwitcher />
                                </div>
                                <IonButtons slot="end">
                                    {allTasks.length > 0 && (
                                        <TasksFilterControl
                                            filters={filters}
                                            onFiltersChange={setFilters}
                                            tasks={allTasks}
                                            triggerId="tasks-filter-trigger"
                                            iconOnly={isMobile}
                                        />
                                    )}
                                    <AddTaskButton
                                        useIcon
                                        onClick={handleAddTask}
                                        data-id="add-task-button"
                                        iconOnly={isMobile}
                                    />
                                </IonButtons>
                            </>
                        )}
                    </IonToolbar>
                </FullWidthHeader>

                {/* Task Notification Dev Tools */}
                {devSettings.showTaskNotificationTestBtns && (
                    <TasksNotificationDevTools />
                )}

                <TasksList
                    heightOffset={heightOffset}
                    onEditTask={handleEditTask}
                    onDeleteTask={handleDeleteTask}
                    onAddTask={handleAddTask}
                    filteredTasks={filteredTasks}
                    highlightTaskId={highlightTaskId}
                    onHighlightComplete={() => setHighlightTaskId(undefined)}
                />
                <TaskEditorModalComponent
                    modal={taskEditorModal}
                    onSave={handleSaveTask}
                    existingTask={getTaskForEdit()}
                    tasksState={tasksState}
                />
            </InnerhivePage>
        </>
    );
};
